import * as THREE from 'three';

/** Jari-jari lingkaran fade di titik aim (meter, ruang dunia). */
const FADE_RADIUS = 1.4;
/** Kekuatan fade di pusat lingkaran (0 = utuh, 1 = hilang total). */
const FADE_STRENGTH = 0.8;
/** Jarak aim maksimum yang memicu fade. */
const FADE_MAX_DIST = 15;

interface FadeSaved {
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
}

/**
 * Fade lingkaran AIM: objek/wall yang menghalangi garis pandang crosshair
 * (tengah layar) dibuat tembus pandang dalam lingkaran di sekitar titik
 * aim — target di baliknya tetap terlihat. Histeresis 2 frame supaya
 * tepi objek tak berkedip.
 *
 * Hanya mesh bertanda `userData.occluder` (dinding/box/ramp/gate/crate)
 * yang disentuh; lantai, air, dekor, slime, trail tak pernah di-fade.
 */
export class AimFade {
  private raycaster = new THREE.Raycaster();
  private dir = new THREE.Vector3();
  private uniforms = {
    uFadeCenter: { value: new THREE.Vector3() },
    uFadeRadius: { value: FADE_RADIUS },
    uFadeStrength: { value: 0 },
  };
  private current: THREE.Object3D | null = null;
  private pending: THREE.Object3D | null = null;
  private pendingCount = 0;
  private saved = new Map<THREE.Object3D, FadeSaved>();
  private scratch: THREE.Object3D[] = [];

  /** Ray tengah layar; kembalikan mesh occluder terdekat (atau null). */
  private pick(
    camera: THREE.PerspectiveCamera,
    statics: THREE.Object3D[],
    dynamics: Array<{ mesh: THREE.Object3D }>,
  ): { root: THREE.Object3D | null; point: THREE.Vector3 | null } {
    camera.getWorldDirection(this.dir);
    this.scratch.length = 0;
    for (const m of statics) {
      if ((m as THREE.Mesh).userData.occluder) this.scratch.push(m);
    }
    for (const d of dynamics) {
      if (d.mesh.visible && (d.mesh as THREE.Mesh).userData.occluder) {
        this.scratch.push(d.mesh);
      }
    }
    if (this.scratch.length === 0) return { root: null, point: null };
    this.raycaster.set(camera.position, this.dir);
    this.raycaster.far = FADE_MAX_DIST;
    const hits = this.raycaster.intersectObjects(this.scratch, true);
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object;
      while (o && !(o as THREE.Mesh).userData.occluder) o = o.parent;
      if (o) return { root: o, point: h.point };
    }
    return { root: null, point: null };
  }

  update(
    camera: THREE.PerspectiveCamera,
    statics: THREE.Object3D[],
    dynamics: Array<{ mesh: THREE.Object3D }>,
  ): void {
    const { root, point } = this.pick(camera, statics, dynamics);
    if (root === this.current) {
      if (root && point) this.uniforms.uFadeCenter.value.copy(point);
    } else if (root && root === this.pending) {
      this.pendingCount++;
      if (this.pendingCount >= 2) {
        this.setFaded(this.current, false);
        this.current = root;
        this.pending = null;
        this.setFaded(this.current, true);
        if (point) this.uniforms.uFadeCenter.value.copy(point);
      }
    } else if (!root) {
      this.setFaded(this.current, false);
      this.current = null;
      this.pending = null;
      this.uniforms.uFadeStrength.value = 0;
    } else {
      this.pending = root;
      this.pendingCount = 1;
    }
  }

  /** Sedang mem-fade objek apa (untuk test/diagnostik). */
  get faded(): THREE.Object3D | null {
    return this.current;
  }

  private setFaded(mesh: THREE.Object3D | null, on: boolean): void {
    if (!mesh) return;
    const mat = (mesh as THREE.Mesh).material as THREE.MeshStandardMaterial | null;
    if (!mat || typeof mat.opacity !== 'number') return;
    if (on) {
      if (!this.saved.has(mesh)) {
        this.saved.set(mesh, {
          transparent: mat.transparent,
          opacity: mat.opacity,
          depthWrite: mat.depthWrite,
        });
        const self = this;
        mat.onBeforeCompile = (shader) => {
          shader.uniforms.uFadeCenter = self.uniforms.uFadeCenter;
          shader.uniforms.uFadeRadius = self.uniforms.uFadeRadius;
          shader.uniforms.uFadeStrength = self.uniforms.uFadeStrength;
          shader.vertexShader = shader.vertexShader
            .replace(
              '#include <common>',
              '#include <common>\nvarying vec3 vFadeWPos;',
            )
            .replace(
              '#include <project_vertex>',
              '#include <project_vertex>\nvFadeWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
            );
          shader.fragmentShader = shader.fragmentShader
            .replace(
              '#include <common>',
              '#include <common>\nvarying vec3 vFadeWPos;\nuniform vec3 uFadeCenter;\nuniform float uFadeRadius;\nuniform float uFadeStrength;',
            )
            .replace(
              '#include <color_fragment>',
              `#include <color_fragment>
              {
                float fadeD = distance(vFadeWPos, uFadeCenter);
                float fadeF = 1.0 - uFadeStrength * (1.0 - smoothstep(uFadeRadius * 0.55, uFadeRadius, fadeD));
                diffuseColor.a *= fadeF;
              }`,
            );
        };
        mat.customProgramCacheKey = () => 'aim-fade-circle-v1';
        mat.needsUpdate = true;
      }
      mat.transparent = true;
      mat.depthWrite = false;
      this.uniforms.uFadeStrength.value = FADE_STRENGTH;
    } else {
      const rec = this.saved.get(mesh);
      if (rec) {
        mat.transparent = rec.transparent;
        mat.opacity = rec.opacity;
        mat.depthWrite = rec.depthWrite;
      }
    }
  }

  dispose(): void {
    for (const [mesh] of this.saved) this.setFaded(mesh, false);
    this.saved.clear();
    this.current = null;
    this.pending = null;
  }
}
